import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertiesAPI, teamAPI } from '@/lib/api';
import { useSettings } from '@/contexts/SettingsContext';
import { validateName } from '@/lib/validation';
import {
  Plus, Search, Briefcase, Clock, Users, MapPin, Euro, Image as ImageIcon,
  X, ChevronLeft, ChevronRight, Upload, Link as LinkIcon, Sparkles, Tag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// Soft service-style gradients used when no cover image is set
const CARD_GRADIENTS = [
  'from-emerald-400 to-teal-500',
  'from-violet-400 to-purple-500',
  'from-amber-400 to-orange-500',
  'from-sky-400 to-blue-500',
  'from-pink-400 to-rose-500',
  'from-lime-400 to-green-500',
];

const labelize = (s) => (s || '').charAt(0).toUpperCase() + (s || '').slice(1).replace(/_/g, ' ');

export default function PropertiesPage() {
  const [services, setServices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const { settings, formatCurrency, statusMap } = useSettings();
  const navigate = useNavigate();

  const fetchServices = useCallback(async () => {
    try {
      const params = { skip: (page - 1) * limit, limit };
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await propertiesAPI.list(params);
      setServices(res.data.properties);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, statusFilter, page, limit]);

  useEffect(() => { setPage(1); }, [search, typeFilter, statusFilter]);
  useEffect(() => { fetchServices(); }, [fetchServices]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [page]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-5 fade-in" data-testid="properties-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Diensten</h1>
          <p className="text-muted-foreground text-sm">
            {total} {total === 1 ? 'dienst' : 'diensten'}
          </p>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-property-btn" className="gap-2">
          <Plus className="w-4 h-4" /> Nieuwe dienst
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Zoeken op naam of locatie..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="property-search-input"
          />
        </div>
        <select
          value={typeFilter || 'all'}
          onChange={(e) => setTypeFilter(e.target.value === 'all' ? '' : e.target.value)}
          data-testid="type-filter"
          className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
        >
          <option value="all">Alle categorieën</option>
          {(settings.property_types || []).map(t => (
            <option key={t} value={t}>{labelize(t)}</option>
          ))}
        </select>
        <select
          value={statusFilter || 'all'}
          onChange={(e) => setStatusFilter(e.target.value === 'all' ? '' : e.target.value)}
          data-testid="status-filter"
          className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
        >
          <option value="all">Alle statussen</option>
          {(settings.property_statuses || []).map(s => {
            const key = typeof s === 'object' ? s.key : s;
            const label = typeof s === 'object' ? s.label : labelize(s);
            return <option key={key} value={key}>{label}</option>;
          })}
        </select>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
        {services.map((svc, i) => (
          <ServiceCard
            key={svc.id}
            service={svc}
            index={i}
            statusMap={statusMap}
            formatCurrency={formatCurrency}
            onClick={() => navigate(`/properties/${svc.id}`)}
          />
        ))}
      </div>

      {services.length === 0 && !loading && (
        <div className="text-center py-16 text-muted-foreground">
          <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nog geen diensten. Klik op "Nieuwe dienst" om je eerste dienst toe te voegen.</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            Toont {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} van {total} diensten
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1} className="gap-1">
              <ChevronLeft className="w-4 h-4" /> Vorige
            </Button>
            <span className="text-sm text-muted-foreground px-2">
              Pagina {page} van {totalPages}
            </span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="gap-1">
              Volgende <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <AddServiceDialog open={showAdd} onClose={() => setShowAdd(false)} onSuccess={fetchServices} />
    </div>
  );
}

// ─── Service Card ───
function ServiceCard({ service, index, statusMap, formatCurrency, onClick }) {
  const cover = service.photos?.[0];
  const gradient = CARD_GRADIENTS[index % CARD_GRADIENTS.length];
  const statusInfo = statusMap[service.status] || { label: service.status, color: '#6B7280' };

  return (
    <Card
      className="border-border/60 overflow-hidden group cursor-pointer hover:shadow-lg transition-all duration-200"
      onClick={onClick}
      data-testid={`property-card-${service.id}`}
    >
      <div className="relative h-32 overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={service.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add(`bg-gradient-to-br`, ...gradient.split(' ')); }}
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
            <Briefcase className="w-12 h-12 text-white/40" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
          <Badge className="text-[10px] text-white border-0" style={{ backgroundColor: statusInfo.color }}>
            {statusInfo.label}
          </Badge>
          {service.type && (
            <Badge variant="secondary" className="text-[10px] backdrop-blur-sm bg-black/50 text-white border-0">
              {labelize(service.type)}
            </Badge>
          )}
        </div>
      </div>

      <CardContent className="p-4 space-y-2">
        <h3 className="font-semibold text-sm leading-tight line-clamp-1">{service.title}</h3>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {service.address && (
            <span className="flex items-center gap-1 line-clamp-1">
              <MapPin className="w-3 h-3 flex-shrink-0" />{service.address}
            </span>
          )}
          {service.duration && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />{service.duration}
            </span>
          )}
          {service.max_participants != null && service.max_participants > 0 && (
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />max {service.max_participants}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-1">
          {service.price > 0 ? (
            <span className="text-sm font-semibold text-emerald-700 flex items-center gap-0.5">
              <Euro className="w-3.5 h-3.5" />{formatCurrency(service.price).replace('€', '').trim()}
            </span>
          ) : <span className="text-xs text-muted-foreground italic">Prijs op aanvraag</span>}

          <div className="flex items-center gap-2">
            {service.assigned_to_name && (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary text-[10px] font-semibold" title={service.assigned_to_name}>
                {service.assigned_to_name.charAt(0).toUpperCase()}
              </span>
            )}
            {service.lead_count > 0 && (
              <span className="text-[10px] text-primary font-medium flex items-center gap-1">
                <Users className="w-3 h-3" />{service.lead_count} {service.lead_count === 1 ? 'lead' : 'leads'}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Add Service Dialog ───
function AddServiceDialog({ open, onClose, onSuccess }) {
  const { settings } = useSettings();
  const defaultType = (settings.property_types || ['workshop'])[0] || 'workshop';
  const defaultStatus = (() => {
    const arr = settings.property_statuses || [];
    const first = arr[0];
    return typeof first === 'object' ? first.key : (first || 'beschikbaar');
  })();

  const [form, setForm] = useState({
    title: '', address: '', type: defaultType, price: '', duration: '',
    max_participants: '', status: defaultStatus, description: '', photos: [],
    assigned_to: '', custom_fields: {},
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [photoInput, setPhotoInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('file');
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    if (open) {
      setCustomFieldDefs(settings.custom_fields_properties || []);
      setErrors({});
      setForm({
        title: '', address: '', type: defaultType, price: '', duration: '',
        max_participants: '', status: defaultStatus, description: '', photos: [],
        assigned_to: '', custom_fields: {},
      });
      setPhotoInput('');
      teamAPI.members().then(res => setTeamMembers(res.data || [])).catch(() => setTeamMembers([]));
    }
  }, [open, settings, defaultType, defaultStatus]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const valid = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!valid.includes(file.type)) {
      toast.error('Ongeldig bestandstype. Gebruik JPG, PNG, WEBP of GIF');
      e.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bestand te groot. Maximaal 10MB');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await propertiesAPI.uploadPhoto(formData);
      setForm(p => ({ ...p, photos: [...p.photos, res.data.url] }));
      toast.success('Afbeelding geüpload');
      e.target.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const addPhotoUrl = () => {
    const url = photoInput.trim();
    if (!url) { toast.error('Vul een URL in'); return; }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      toast.error('URL moet beginnen met http:// of https://');
      return;
    }
    if (form.photos.includes(url)) { toast.error('Deze URL is al toegevoegd'); return; }
    setForm(p => ({ ...p, photos: [...p.photos, url] }));
    setPhotoInput('');
    toast.success('Afbeelding toegevoegd');
  };

  const removePhoto = (idx) => {
    setForm(p => ({ ...p, photos: p.photos.filter((_, i) => i !== idx) }));
  };

  const validate = () => {
    const e = {};
    const titleErr = validateName(form.title, 'Naam', { min: 2, max: 200, allowNumbers: true });
    if (titleErr) e.title = titleErr;
    if (form.price && Number(form.price) < 0) e.price = 'Tarief moet positief zijn';
    if (form.max_participants && (Number(form.max_participants) < 0 || Number(form.max_participants) > 10000)) {
      e.max_participants = 'Tussen 0 en 10.000';
    }
    if (form.description && form.description.length > 5000) e.description = 'Max 5000 karakters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await propertiesAPI.create({
        ...form,
        title: form.title.trim(),
        address: form.address?.trim() || null,
        price: form.price ? parseFloat(form.price) : 0,
        duration: form.duration?.trim() || null,
        max_participants: form.max_participants ? parseInt(form.max_participants) : null,
      });
      toast.success('Dienst aangemaakt');
      onSuccess();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Aanmaken mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (f) => errors[f] ? <p className="text-[10px] text-destructive mt-0.5">{errors[f]}</p> : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="add-property-dialog">
        <DialogHeader><DialogTitle>Nieuwe dienst</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Naam <span className="text-destructive">*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="bijv. Workshop Vitaliteit & Energie"
                className={errors.title ? 'border-destructive' : ''}
                data-testid="property-title-input"
              />
              {fieldError('title')}
            </div>

            <div>
              <Label className="flex items-center gap-1"><Tag className="w-3 h-3" /> Categorie</Label>
              <select
                value={form.type}
                onChange={(e) => setForm(p => ({ ...p, type: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
              >
                {(settings.property_types || ['workshop']).map(t => (
                  <option key={t} value={t}>{labelize(t)}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Status</Label>
              <select
                value={form.status}
                onChange={(e) => setForm(p => ({ ...p, status: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
              >
                {(settings.property_statuses || []).map(s => {
                  const key = typeof s === 'object' ? s.key : s;
                  const label = typeof s === 'object' ? s.label : labelize(s);
                  return <option key={key} value={key}>{label}</option>;
                })}
              </select>
            </div>

            <div className="col-span-2">
              <Label className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Locatie <span className="text-xs text-muted-foreground font-normal">(optioneel)</span></Label>
              <Input
                value={form.address}
                onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))}
                placeholder="bijv. Op kantoor klant, Amsterdam, of online"
                data-testid="property-address-input"
              />
            </div>

            <div>
              <Label className="flex items-center gap-1"><Euro className="w-3 h-3" /> Tarief <span className="text-xs text-muted-foreground font-normal">(optioneel)</span></Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))}
                placeholder="0 = op aanvraag"
                className={errors.price ? 'border-destructive' : ''}
                data-testid="property-price-input"
              />
              {fieldError('price')}
            </div>

            <div>
              <Label className="flex items-center gap-1"><Clock className="w-3 h-3" /> Duur <span className="text-xs text-muted-foreground font-normal">(optioneel)</span></Label>
              <Input
                value={form.duration}
                onChange={(e) => setForm(p => ({ ...p, duration: e.target.value }))}
                placeholder="bijv. 90 min, halve dag, 2 dagen"
              />
            </div>

            <div>
              <Label className="flex items-center gap-1"><Users className="w-3 h-3" /> Max deelnemers <span className="text-xs text-muted-foreground font-normal">(optioneel)</span></Label>
              <Input
                type="number"
                min="0"
                max="10000"
                value={form.max_participants}
                onChange={(e) => setForm(p => ({ ...p, max_participants: e.target.value }))}
                placeholder="bijv. 25"
              />
              {fieldError('max_participants')}
            </div>

            {teamMembers.length > 0 && (
              <div>
                <Label>Verantwoordelijke</Label>
                <select
                  value={form.assigned_to || ''}
                  onChange={(e) => setForm(p => ({ ...p, assigned_to: e.target.value }))}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                >
                  <option value="">Niet toegewezen</option>
                  {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                </select>
              </div>
            )}

            <div className="col-span-2">
              <Label>Beschrijving</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                rows={4}
                maxLength={5000}
                placeholder="Wat houdt deze dienst in? Doel, doelgroep, opzet..."
              />
              {fieldError('description')}
            </div>

            {/* Afbeelding (cover) */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <Label className="flex items-center gap-2 mb-3">
                <ImageIcon className="w-4 h-4" /> Afbeelding <span className="text-xs text-muted-foreground font-normal">(optioneel)</span>
              </Label>
              <div className="flex gap-2 mb-3 border-b">
                <button type="button" onClick={() => setUploadMode('file')}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${uploadMode === 'file' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                  <Upload className="w-4 h-4 inline mr-1" /> Uploaden
                </button>
                <button type="button" onClick={() => setUploadMode('url')}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${uploadMode === 'url' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                  <LinkIcon className="w-4 h-4 inline mr-1" /> Vanaf URL
                </button>
              </div>

              {uploadMode === 'file' && (
                <div className="mb-3">
                  <div className="flex items-center gap-2">
                    <Input type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      onChange={handleFileUpload} disabled={uploading} className="flex-1" />
                    {uploading && <span className="text-sm text-muted-foreground">Uploaden...</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">JPG, PNG, WEBP of GIF (max 10MB)</p>
                </div>
              )}

              {uploadMode === 'url' && (
                <div className="mb-3">
                  <div className="flex gap-2">
                    <Input placeholder="https://..." value={photoInput}
                      onChange={(e) => setPhotoInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPhotoUrl(); } }}
                      className="flex-1" />
                    <Button type="button" variant="outline" size="sm" onClick={addPhotoUrl}>Toevoegen</Button>
                  </div>
                </div>
              )}

              {form.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {form.photos.map((url, idx) => (
                    <div key={idx} className="relative group aspect-video rounded-md overflow-hidden border bg-muted">
                      <img src={url} alt={`Afbeelding ${idx + 1}`} className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.opacity = '0.3'; }} />
                      <button type="button" onClick={() => removePhoto(idx)}
                        className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {customFieldDefs.length > 0 && (
              <>
                <div className="col-span-2 border-t pt-3 mt-1">
                  <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Extra velden
                  </p>
                </div>
                {customFieldDefs.map((cf) => (
                  <div key={cf.key} className={cf.type === 'textarea' ? 'col-span-2' : ''}>
                    <Label className="text-xs">{cf.label} {cf.required && <span className="text-destructive">*</span>}</Label>
                    <CustomServiceFieldInput field={cf}
                      value={form.custom_fields?.[cf.key] || ''}
                      onChange={(val) => setForm(p => ({ ...p, custom_fields: { ...(p.custom_fields || {}), [cf.key]: val } }))} />
                  </div>
                ))}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Annuleren</Button>
            <Button type="submit" disabled={saving} data-testid="save-property-btn">
              {saving ? 'Opslaan...' : 'Dienst toevoegen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CustomServiceFieldInput({ field, value, onChange }) {
  switch (field.type) {
    case 'select':
      return (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="">Selecteer...</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case 'textarea':
      return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2} className="text-xs" placeholder={field.placeholder} required={field.required} />;
    case 'checkbox':
      return (
        <div className="flex items-center gap-2 pt-1">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="rounded border-input" />
          <span className="text-xs text-muted-foreground">{field.placeholder || 'Ja'}</span>
        </div>
      );
    case 'number':
      return <Input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)} className="text-xs" placeholder={field.placeholder} required={field.required} />;
    case 'date':
      return <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className="text-xs" required={field.required} />;
    default:
      return <Input type={field.type || 'text'} value={value || ''} onChange={(e) => onChange(e.target.value)} className="text-xs" placeholder={field.placeholder} required={field.required} />;
  }
}
